/**
 * Task API endpoints
 */
import axiosClient from './axiosClient';
import {
  Task, TaskWithDetails, CreateTaskData, UpdateTaskData, TaskFilters,
  Subtask, CreateSubtaskData, UpdateSubtaskData,
  BulkTaskUpdate, BulkTaskDelete, Activity, Attachment, TaskWorklog
} from '@/types';

const updateTaskDescription = (taskId: string, data: { description: string }) =>
  axiosClient.put(`/tasks/${taskId}/description`, data);

const getTaskSubtasks = (taskId: string) =>
  axiosClient.get(`/tasks/${taskId}/subtasks`);

const createSubtask = (taskId: string, data: any) =>
  axiosClient.post(`/tasks/${taskId}/subtasks`, data);

const updateSubtask = (subtaskId: string, data: any) =>
  axiosClient.put(`/subtasks/${subtaskId}`, data);

// ✅ Clean request before sending – strips undefined/empty strings but preserves null for specific fields
const cleanPayload = (obj: any) =>
  Object.fromEntries(
    Object.entries(obj).filter(([k, v]) => {
      if (v === undefined) return false;
      // Preserve null for fields where null has semantic meaning (e.g., unassigning a user)
      if (v === null) return k === 'assigned_to' || k === 'sprint_id';
      if (typeof v === 'string' && v === '') return false;
      return true;
    })
  );


export const taskAPI = {
  /**
   * Get all tasks with optional filters
   */
  // getTasks: async (filters?: TaskFilters & { skip?: number; limit?: number }): Promise<Task[]> => {
  //   const response = await axiosClient.get<Task[]>('/tasks', { params: filters });
  //   return response.data;
  // },
  getTasks: async (filters?: TaskFilters & { skip?: number; limit?: number }): Promise<Task[]> => {
    const { project_ids, ...rest } = filters || {};
    
    const params = new URLSearchParams();
    
    // Serialize project_ids as repeated params
    if (project_ids?.length) {
      project_ids.forEach(id => params.append('project_ids', id));
    }
    
    // Serialize remaining filters normally
    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
  
    const response = await axiosClient.get<Task[]>('/tasks', { params });
    return response.data;
  },

  /**
   * Get calendar tasks with date range filtering
   */
  getCalendarTasks: async (params?: {
    project_id?: string;
    start_date?: string; // ISO date string YYYY-MM-DD
    end_date?: string; // ISO date string YYYY-MM-DD
    assigned_to?: string;
    workflow_status_id?: string;
    priority_id?: string;
    task_type_id?: string;
  }): Promise<Task[]> => {
    const response = await axiosClient.get<Task[]>('/tasks/calendar', { params });
    return response.data;
  },

  /**
   * Get backlog tasks
   */
  getBacklogTasks: async (projectId?: string): Promise<Task[]> => {
    const response = await axiosClient.get<Task[]>('/tasks/backlog', {
      params: { project_id: projectId },
    });
    return response.data;
  },

  /**
   * Get recent tasks for the current user, sorted by updated_at (most recent first)
   */
  getRecentTasks: async (limit?: number, skip?: number, project_ids?: string[]): Promise<Task[]> => {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', String(limit));
    if (skip !== undefined) params.append('skip', String(skip));
    if (project_ids?.length) {
      project_ids.forEach(id => params.append('project_ids', id));
    }
    const response = await axiosClient.get<Task[]>(`/tasks/recent?${params.toString()}`);
    return response.data;
  },

  /**
   * Get task by ID with full details
   */
  getTask: async (id: string): Promise<TaskWithDetails> => {
    const response = await axiosClient.get<TaskWithDetails>(`/tasks/${id}`);
    return response.data;
  },

  /**
   * Get task with complete details including related data
   */
  getTaskWithDetails: async (id: string): Promise<TaskWithDetails> => {
    const response = await axiosClient.get<TaskWithDetails>(`/tasks/${id}/details`);
    return response.data;
  },

  /**
   * Create new task
   */
  createTask: async (data: CreateTaskData): Promise<Task> => {
    const response = await axiosClient.post<Task>('/tasks', data);
    return response.data;
  },

  /**
   * Update task
   */
  // updateTask: async (id: string, data: UpdateTaskData): Promise<Task> => {
  //   const response = await axiosClient.put<Task>(`/tasks/${id}`, data);
  //   return response.data;
  // },

  updateTask: async (id: string, data: UpdateTaskData): Promise<Task> => {
    const payload = cleanPayload({
      ...data,
      labels: Array.isArray(data.labels) ? data.labels : undefined
    });

    const response = await axiosClient.put<Task>(`/tasks/${id}`, payload);
    return response.data;
  },


  /**
   * Update task description
   */
  updateTaskDescription: async (id: string, description: string): Promise<Task> => {
    const response = await axiosClient.put<Task>(`/tasks/${id}/description`, { description });
    return response.data;
  },

  /**
   * Update task workflow status and board order via dedicated endpoint
   */
  updateTaskStatusAdvanced: async (id: string, toStatusId: string, boardOrder?: number): Promise<Task> => {
    const params: any = { to_status_id: toStatusId };
    if (typeof boardOrder === 'number') params.board_order = boardOrder;
    const response = await axiosClient.put<Task>(`/tasks/${id}/status`, undefined, { params });
    return response.data;
  },

  /**
   * Delete task
   */
  deleteTask: async (id: string): Promise<void> => {
    await axiosClient.delete(`/tasks/${id}`);
  },

  /**
   * Archive task
   */
  archiveTask: async (id: string): Promise<Task> => {
    const response = await axiosClient.post<Task>(`/tasks/${id}/archive`);
    return response.data;
  },

  /**
   * Unarchive task
   */
  unarchiveTask: async (id: string): Promise<Task> => {
    const response = await axiosClient.post<Task>(`/tasks/${id}/unarchive`);
    return response.data;
  },

  /**
   * Bulk update tasks
   */
  bulkUpdateTasks: async (data: BulkTaskUpdate): Promise<Task[]> => {
    const response = await axiosClient.post<Task[]>('/tasks/bulk-update', data);
    return response.data;
  },

  /**
   * Bulk delete tasks
   */
  bulkDeleteTasks: async (data: BulkTaskDelete): Promise<void> => {
    await axiosClient.delete('/tasks/bulk-delete', { data });
  },

  // ==================== COMMENTS SECTION ====================

  /**
   * Get task comments
   */
  getTaskComments: async (taskId: string): Promise<Comment[]> => {
    const response = await axiosClient.get<Comment[]>(`/tasks/${taskId}/comments`);
    return response.data;
  },

  /**
   * Add comment to task
   */
  addComment: async (taskId: string, content: string, parentId?: string, isInternal: boolean = false): Promise<Comment> => {
    const response = await axiosClient.post<Comment>(`/tasks/${taskId}/comments`, {
      content,
      is_internal: isInternal,
      parent_comment_id: parentId,
    });
    return response.data;
  },

  /**
   * Update comment
   */
  updateComment: async (commentId: string, content: string): Promise<Comment> => {
    const response = await axiosClient.put<Comment>(`/comments/${commentId}`, { content });
    return response.data;
  },

  /**
   * Delete comment
   */
  deleteComment: async (commentId: string): Promise<void> => {
    await axiosClient.delete(`/comments/${commentId}`);
  },

  // ==================== SUBTASKS SECTION ====================

  /**
   * Get ALL subtasks
   */
  getAllSubtasks: async (filters?: TaskFilters & { skip?: number; limit?: number }): Promise<Subtask[]> => {
    const response = await axiosClient.get<Subtask[]>('/subtasks/all-subtasks', { params: filters });
    return response.data;
  },

  /**
   * Get recent subtasks for the current user, sorted by updated_at (most recent first)
   */
  getRecentSubtasks: async (limit?: number, skip?: number): Promise<Subtask[]> => {
    const response = await axiosClient.get<Subtask[]>('/subtasks/recent', {
      params: { limit, skip },
    });
    return response.data;
  },

  /**
   * Create subtask
   */
  createSubtask: async (taskId: string, data: CreateSubtaskData): Promise<Subtask> => {
    const response = await axiosClient.post<Subtask>(`/tasks/${taskId}/subtasks`, data);
    return response.data;
  },

  /**
   * Get subtasks for a task
   */
  getSubtasks: async (taskId: string): Promise<Subtask[]> => {
    const response = await axiosClient.get<Subtask[]>(`/tasks/${taskId}/subtasks`);
    return response.data;
  },

  /**
   * Update subtask
   */
  updateSubtask: async (subtaskId: string, data: UpdateSubtaskData): Promise<Subtask> => {
    const response = await axiosClient.put<Subtask>(`/subtasks/${subtaskId}`, data);
    return response.data;
  },

  /**
   * Delete subtask
   */
  deleteSubtask: async (subtaskId: string): Promise<void> => {
    await axiosClient.delete(`/subtasks/${subtaskId}`);
  },

  // ==================== ACTIVITY & HISTORY SECTION ====================

  /**
   * Get task activity log
   */
  getTaskActivity: async (taskId: string): Promise<Activity[]> => {
    const response = await axiosClient.get<Activity[]>(`/tasks/${taskId}/activity`);
    return response.data;
  },

  /**
   * Get task history
   */
  getTaskHistory: async (taskId: string): Promise<Activity[]> => {
    const response = await axiosClient.get<Activity[]>(`/tasks/${taskId}/history`);
    return response.data;
  },

  // ==================== ATTACHMENTS SECTION ====================

  /**
   * Get task attachments
   */
  getTaskAttachments: async (taskId: string): Promise<Attachment[]> => {
    const response = await axiosClient.get<Attachment[]>(`/tasks/${taskId}/attachments`);
    return response.data;
  },

  /**
   * Upload attachment to task
   */
  uploadAttachment: async (taskId: string, file: File): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosClient.post<Attachment>(
      `/tasks/${taskId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Delete attachment
   */
  deleteAttachment: async (attachmentId: string): Promise<void> => {
    await axiosClient.delete(`/attachments/${attachmentId}`);
  },

  /**
   * Download attachment
   */
  downloadAttachment: async (attachmentId: string): Promise<Blob> => {
    const response = await axiosClient.get(`/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // ==================== RELATED WORK SECTION ====================

  /**
   * Get related work items (blocks and relates to)
   */
  getRelatedWork: async (taskId: string): Promise<{
    blocks: Task[];
    relatesTo: Task[];
  }> => {
    const response = await axiosClient.get<{
      blocks: Task[];
      relatesTo: Task[];
    }>(`/tasks/${taskId}/related-work`);
    return response.data;
  },

  /**
   * Add related work item
   */
  addRelatedWork: async (taskId: string, relatedTaskId: string, relationType: 'blocks' | 'relates_to'): Promise<void> => {
    await axiosClient.post(`/tasks/${taskId}/related-work`, {
      related_task_id: relatedTaskId,
      relation_type: relationType,
    });
  },

  /**
   * Remove related work item
   */
  removeRelatedWork: async (taskId: string, relatedTaskId: string, relationType: 'blocks' | 'relates_to'): Promise<void> => {
    await axiosClient.delete(`/tasks/${taskId}/related-work`, {
      data: {
        related_task_id: relatedTaskId,
        relation_type: relationType,
      },
    });
  },

  // ==================== WORKLOGS SECTION ====================

  /**
   * Get task worklogs
   */
  getTaskWorklogs: async (taskId: string): Promise<TaskWorklog[]> => {
    const response = await axiosClient.get<TaskWorklog[]>(`/tasks/${taskId}/worklogs`);
    return response.data;
  },

  /**
   * Add worklog to task
   */
  addWorklog: async (taskId: string, data: {
    time_spent_minutes: number;
    started_at?: string;
    comment?: string;
    adjust_remaining?: 'auto' | 'set' | 'none';
    remaining_time_minutes?: number | null;
  }): Promise<{ worklog: TaskWorklog; task: Task }> => {
    const payload = {
      adjust_remaining: data.adjust_remaining ?? 'auto',
      ...data,
    };
    const response = await axiosClient.post<{ worklog: TaskWorklog; task: Task }>(`/tasks/${taskId}/worklogs`, payload);
    return response.data;
  },

  // ==================== EXPORT/IMPORT SECTION ====================

  /**
   * Export tasks to CSV
   */
  exportTasksCSV: async (projectId?: string): Promise<Blob> => {
    const response = await axiosClient.get('/tasks/export/csv', {
      params: { project_id: projectId },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Import tasks from CSV
   */
  importTasksCSV: async (file: File, projectId: string): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);

    const response = await axiosClient.post('/tasks/import/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // ==================== SEARCH SECTION ====================

  /**
   * Search tasks
   */
  searchTasks: async (query: string, projectId?: string): Promise<Task[]> => {
    const response = await axiosClient.get<Task[]>('/tasks/search', {
      params: { q: query, project_id: projectId },
    });
    return response.data;
  },

  /**
   * Quick task search for assignee dropdown
   */
  searchUsersForAssignment: async (query: string, projectId?: string): Promise<any[]> => {
    const response = await axiosClient.get<any[]>('/users/search', {
      params: { q: query, project_id: projectId, limit: 20 },
    });
    return response.data;
  },

  /**
   * Assign task to sprint
   */
  assignToSprint: async (taskId: string, sprintId: string | null): Promise<Task> => {
    const response = await axiosClient.put<Task>(`/tasks/${taskId}`, {
      sprint_id: sprintId
    });
    return response.data;
  },
getTaskAssigneeChanges: async (taskId: string): Promise<any> => {
    const response = await axiosClient.get<any>(`/tasks/${taskId}/assignee-changes`);
    return response.data;
  },
  getTaskSubtasks
};